import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Users } from "lucide-react";
import {
  DayButton as DayPickerDayButton,
  DayPicker,
  UI,
  type DayButtonProps,
  useDayPicker,
} from "react-day-picker";
import { es } from "date-fns/locale";
import type { VacationRequest } from "../types/admin-panel";
import { getCompanyRestDays, type CompanyRestDay } from "../api/admin-panel";
import "react-day-picker/dist/style.css";

type Props = {
  vacations: VacationRequest[];
  legendVacations?: VacationRequest[];
  selectedMonth: string;
  selectedYear: string;
  onSelectVacation?: (vacationId: number | null) => void;
};

type CalendarEntry = {
  vacationId: number;
  employee: string;
  status: VacationRequest["estado"];
  jornada: VacationRequest["dias"][number]["jornada"];
  cantidadDias: number;
  requestStart: string;
  requestEnd: string;
};

export function AdminVacationCalendar({
  vacations,
  legendVacations,
  selectedMonth,
  selectedYear,
  onSelectVacation,
}: Props) {
  const [month, setMonth] = useState(() => resolveCalendarMonth(selectedMonth, selectedYear));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedVacationId, setSelectedVacationId] = useState<number | null>(null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [restDays, setRestDays] = useState<CompanyRestDay[]>([]);
  const tooltipCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTooltipCloseTimer = () => {
    if (tooltipCloseTimerRef.current !== null) {
      clearTimeout(tooltipCloseTimerRef.current);
      tooltipCloseTimerRef.current = null;
    }
  };

  const scheduleTooltipClose = () => {
    clearTooltipCloseTimer();
    tooltipCloseTimerRef.current = setTimeout(() => {
      setHoveredDate(null);
      tooltipCloseTimerRef.current = null;
    }, 900);
  };

  const entriesByDate = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();

    vacations.forEach((vacation) => {
      vacation.dias.forEach((day) => {
        if (selectedMonth !== "all" && day.date.slice(5, 7) !== selectedMonth) return;
        if (selectedYear !== "all" && day.date.slice(0, 4) !== selectedYear) return;

        const entries = map.get(day.date) ?? [];
        entries.push({
          vacationId: vacation.id,
          employee: vacation.empleado ?? "Usuario",
          status: vacation.estado,
          jornada: day.jornada,
          cantidadDias: day.cantidad_dias,
          requestStart: vacation.fecha_inicio,
          requestEnd: vacation.fecha_fin,
        });
        map.set(day.date, entries);
      });
    });

    return map;
  }, [selectedMonth, selectedYear, vacations]);

  const legendMonths = useMemo(
    () => buildLegendMonths(legendVacations ?? vacations),
    [legendVacations, vacations],
  );

  const modifiers = useMemo(() => {
    const approved: Date[] = [];
    const pending: Date[] = [];
    const rejected: Date[] = [];
    const mixed: Date[] = [];
    const detailSelected: Date[] = [];
    const restday = restDays.map((item) => parseYmd(item.fecha));

    entriesByDate.forEach((entries, date) => {
      const statuses = new Set(entries.map((entry) => entry.status));
      const parsed = parseYmd(date);
      if (statuses.size > 1) mixed.push(parsed);
      else if (statuses.has("aprobado")) approved.push(parsed);
      else if (statuses.has("pendiente")) pending.push(parsed);
      else if (statuses.has("rechazado")) rejected.push(parsed);
    });

    if (selectedDate && entriesByDate.has(selectedDate)) {
      detailSelected.push(parseYmd(selectedDate));
    }

    return { approved, pending, rejected, mixed, detailSelected, restday };
  }, [entriesByDate, restDays, selectedDate]);

  useEffect(() => {
    return () => {
      if (tooltipCloseTimerRef.current !== null) {
        clearTimeout(tooltipCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedMonth === "all" && selectedYear === "all") return;
    setMonth(resolveCalendarMonth(selectedMonth, selectedYear));
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    let active = true;
    void getCompanyRestDays(month.getFullYear())
      .then((items) => {
        if (active) setRestDays(items);
      })
      .catch(() => {
        if (active) setRestDays([]);
      });
    return () => {
      active = false;
    };
  }, [month]);

  useEffect(() => {
    if (selectedDate && !entriesByDate.has(selectedDate)) {
      setSelectedDate(null);
      setSelectedVacationId(null);
    }
    if (hoveredDate && !entriesByDate.has(hoveredDate)) {
      setHoveredDate(null);
    }
  }, [entriesByDate, hoveredDate, selectedDate]);

  const selectedEntries = selectedDate ? entriesByDate.get(selectedDate) ?? [] : [];

  useEffect(() => {
    if (
      selectedVacationId !== null &&
      !selectedEntries.some((entry) => entry.vacationId === selectedVacationId)
    ) {
      setSelectedVacationId(null);
    }
  }, [selectedEntries, selectedVacationId]);

  const calendarBounds = useMemo(() => {
    const years = vacations.flatMap((vacation) =>
      vacation.dias.map((day) => Number(day.date.slice(0, 4))),
    );
    const currentYear = new Date().getFullYear();
    const minYear = years.length > 0 ? Math.min(...years, currentYear) : currentYear - 5;
    const maxYear = years.length > 0 ? Math.max(...years, currentYear + 1) : currentYear + 1;
    return {
      start: new Date(minYear, 0, 1),
      end: new Date(maxYear, 11, 1),
    };
  }, [vacations]);

  const CalendarDayButton = (props: DayButtonProps) => {
    const { classNames } = useDayPicker();
    const iso = toYmd(props.day.date);
    const entries = entriesByDate.get(iso) ?? [];
    const restDay = restDays.find((item) => item.fecha === iso);
    const userCount = countUniqueEmployees(entries);
    const isTooltipOpen = hoveredDate === iso && entries.length > 0;
    const weekDay = props.day.date.getDay();
    const tooltipEdgeClass =
      weekDay === 1 ? " is-edge-left" : weekDay === 0 ? " is-edge-right" : "";

    return (
      <DayPickerDayButton
        {...props}
        className={`${classNames[UI.DayButton]} admin-vacation-day-button${entries.length > 0 ? " has-vacations" : ""}`}
        aria-label={buildAccessibleDayLabel(iso, entries)}
        title={[entries.length > 0 ? buildTooltipText(iso, entries) : null, restDay ? `Descanso de plantilla: ${restDay.nombre}` : null].filter(Boolean).join(" · ") || undefined}
      >
        <span className="admin-vacation-day-number">{props.day.date.getDate()}</span>

        {restDay && (
          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-sky-100 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-sky-700">
            Descanso
          </span>
        )}

        {userCount > 0 && (
          <span className="admin-vacation-day-user-count" aria-hidden="true">
            <Users size={11} strokeWidth={2.4} />
            <span>+{userCount}</span>
          </span>
        )}

        {isTooltipOpen && (
          <span
            className={`admin-vacation-day-tooltip${tooltipEdgeClass}`}
            role="dialog"
            aria-label={`Solicitudes del ${formatDate(iso)}`}
            onMouseEnter={clearTooltipCloseTimer}
            onMouseLeave={scheduleTooltipClose}
          >
            <span className="admin-vacation-day-tooltip-date">{formatDate(iso)}</span>
            {entries.map((entry) => {
              const range = requestRangeLabel(entry.requestStart, entry.requestEnd);
              const isSelected =
                selectedDate === iso && selectedVacationId === entry.vacationId;

              return (
                <span
                  key={`tooltip-${entry.vacationId}-${entry.employee}-${entry.jornada}`}
                  className={`admin-vacation-day-tooltip-entry${isSelected ? " is-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedDate(iso);
                    setSelectedVacationId(entry.vacationId);
                    onSelectVacation?.(entry.vacationId);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedDate(iso);
                    setSelectedVacationId(entry.vacationId);
                    onSelectVacation?.(entry.vacationId);
                  }}
                >
                  <span
                    className={`admin-vacation-day-status-dot ${statusDotClass(entry.status)}`}
                  />
                  <span>
                    <strong>{entry.employee}</strong>
                    <span>
                      {statusLabel(entry.status)} · {jornadaLabel(entry.jornada)}
                      {range ? ` · ${range}` : ""}
                    </span>
                  </span>
                </span>
              );
            })}
          </span>
        )}
      </DayPickerDayButton>
    );
  };

  return (
    <section className="rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,.05)]">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
            <CalendarDays size={18} />
          </span>
          <div>
            <div className="text-sm font-black text-slate-950">Calendario del equipo</div>
            <div className="mt-1 text-xs font-semibold text-slate-500">
              Vacaciones visibles según los filtros seleccionados.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs font-black">
          <Legend
            className="bg-emerald-100 text-emerald-700"
            label="Aprobadas"
            months={legendMonths.approved}
            tooltipLabel="Meses con solicitudes aprobadas"
          />
          <Legend
            className="bg-amber-100 text-amber-700"
            label="Pendientes"
            months={legendMonths.pending}
            tooltipLabel="Meses con solicitudes pendientes"
          />
          <Legend
            className="bg-rose-100 text-rose-700"
            label="Rechazadas"
            months={legendMonths.rejected}
            tooltipLabel="Meses con solicitudes rechazadas"
          />
          <Legend
            className="bg-sky-100 text-sky-700"
            label="Varios estados"
            months={legendMonths.mixed}
            tooltipLabel="Meses con días que combinan varios estados"
          />
        </div>
      </div>

      <div className="admin-vacation-calendar">
        <DayPicker
          locale={es}
          weekStartsOn={1}
          month={month}
          onMonthChange={setMonth}
          showOutsideDays
          captionLayout="dropdown"
          startMonth={calendarBounds.start}
          endMonth={calendarBounds.end}
          modifiers={modifiers}
          modifiersClassNames={{
            approved: "admin-vacation-day-approved",
            pending: "admin-vacation-day-pending",
            rejected: "admin-vacation-day-rejected",
            mixed: "admin-vacation-day-mixed",
            detailSelected: "admin-vacation-day-selected",
            restday: "bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200",
          }}
          onDayClick={(day) => {
            const iso = toYmd(day);
            if (entriesByDate.has(iso)) {
              setSelectedDate(iso);
              setSelectedVacationId(null);
              onSelectVacation?.(null);
            }
          }}
          onDayMouseEnter={(day) => {
            clearTooltipCloseTimer();
            const iso = toYmd(day);
            setHoveredDate(entriesByDate.has(iso) ? iso : null);
          }}
          onDayMouseLeave={scheduleTooltipClose}
          components={{ DayButton: CalendarDayButton }}
        />
      </div>

      <div className="mt-4 min-h-[64px] rounded-2xl border border-slate-100 bg-slate-50 p-3">
        {selectedEntries.length === 0 ? (
          <div className="text-xs font-semibold text-slate-400">
            Pulsa un día con vacaciones para consultar las personas y estados.
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                {selectedDate ? formatDate(selectedDate) : "Detalle"}
              </div>
              <div className="text-xs font-bold text-slate-400">
                {selectedEntries.length} {selectedEntries.length === 1 ? "solicitud" : "solicitudes"}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedEntries.map((entry) => {
                const isSelected = selectedVacationId === entry.vacationId;

                return (
                  <span
                    key={`${entry.vacationId}-${entry.employee}-${entry.jornada}`}
                    className={`admin-vacation-detail-entry rounded-xl border px-3 py-2 text-xs font-black ${statusClasses(entry.status)}${isSelected ? " is-selected" : ""}`}
                    aria-current={isSelected ? "true" : undefined}
                  >
                    {entry.employee} · {statusLabel(entry.status)} · {jornadaLabel(entry.jornada)} · {formatQuantity(entry.cantidadDias)}
                    {isSelected && (
                      <span className="admin-vacation-detail-selected-label">Seleccionada</span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Legend({
  className,
  label,
  months,
  tooltipLabel,
}: {
  className: string;
  label: string;
  months: string[];
  tooltipLabel: string;
}) {
  return (
    <span className="admin-vacation-legend-item">
      <span
        className={`admin-vacation-legend-badge rounded-full px-3 py-1.5 ${className}`}
        tabIndex={0}
      >
        {label}
      </span>
      <span className="admin-vacation-legend-tooltip" role="tooltip">
        <strong>{tooltipLabel}</strong>
        <span>{months.length > 0 ? formatList(months) : "Sin solicitudes registradas."}</span>
      </span>
    </span>
  );
}

type LegendMonths = {
  approved: string[];
  pending: string[];
  rejected: string[];
  mixed: string[];
};

function buildLegendMonths(vacations: VacationRequest[]): LegendMonths {
  const approved = new Set<string>();
  const pending = new Set<string>();
  const rejected = new Set<string>();
  const mixed = new Set<string>();
  const statusesByDate = new Map<string, Set<VacationRequest["estado"]>>();

  vacations.forEach((vacation) => {
    vacation.dias.forEach((day) => {
      const monthKey = day.date.slice(0, 7);
      if (vacation.estado === "aprobado") approved.add(monthKey);
      else if (vacation.estado === "rechazado") rejected.add(monthKey);
      else pending.add(monthKey);

      const statuses = statusesByDate.get(day.date) ?? new Set<VacationRequest["estado"]>();
      statuses.add(vacation.estado);
      statusesByDate.set(day.date, statuses);
    });
  });

  statusesByDate.forEach((statuses, date) => {
    if (statuses.size > 1) mixed.add(date.slice(0, 7));
  });

  return {
    approved: formatMonthKeys(approved),
    pending: formatMonthKeys(pending),
    rejected: formatMonthKeys(rejected),
    mixed: formatMonthKeys(mixed),
  };
}

function formatMonthKeys(values: Set<string>): string[] {
  return Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => {
      const [year, month] = value.split("-").map(Number);
      return new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(
        new Date(year, month - 1, 1, 12, 0, 0),
      );
    });
}

function formatList(values: string[]): string {
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} y ${values[1]}`;
  return `${values.slice(0, -1).join(", ")} y ${values[values.length - 1]}`;
}

function resolveCalendarMonth(selectedMonth: string, selectedYear: string): Date {
  const now = new Date();
  const year = selectedYear === "all" ? now.getFullYear() : Number(selectedYear);
  const month = selectedMonth === "all" ? now.getMonth() : Number(selectedMonth) - 1;
  return new Date(year, month, 1);
}

function parseYmd(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(parseYmd(value));
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parseYmd(value));
}

function requestRangeLabel(start: string, end: string): string | null {
  if (!start || !end || start === end) return null;
  return `${formatShortDate(start)} al ${formatShortDate(end)}`;
}

function statusLabel(status: VacationRequest["estado"]): string {
  if (status === "aprobado") return "Aprobada";
  if (status === "rechazado") return "Rechazada";
  return "Pendiente";
}

function jornadaLabel(jornada: VacationRequest["dias"][number]["jornada"]): string {
  if (jornada === "manana") return "Mañana";
  if (jornada === "tarde") return "Tarde";
  return "Día completo";
}

function countUniqueEmployees(entries: CalendarEntry[]): number {
  return new Set(entries.map((entry) => entry.employee)).size;
}

function statusDotClass(status: VacationRequest["estado"]): string {
  if (status === "aprobado") return "is-approved";
  if (status === "rechazado") return "is-rejected";
  return "is-pending";
}

function statusClasses(status: VacationRequest["estado"]): string {
  if (status === "aprobado") return "border-emerald-100 bg-emerald-50 text-emerald-700";
  if (status === "rechazado") return "border-rose-100 bg-rose-50 text-rose-700";
  return "border-amber-100 bg-amber-50 text-amber-700";
}

function buildAccessibleDayLabel(date: string, entries: CalendarEntry[]): string {
  if (entries.length === 0) return formatDate(date);
  return buildTooltipText(date, entries).replaceAll(" · ", ", ");
}

function buildTooltipText(date: string, entries: CalendarEntry[]): string {
  return `${formatDate(date)}. ${entries
    .map((entry) => {
      const range = requestRangeLabel(entry.requestStart, entry.requestEnd);
      return `${entry.employee}: ${statusLabel(entry.status)} · ${jornadaLabel(entry.jornada)}${range ? ` · ${range}` : ""}`;
    })
    .join(". ")}`;
}

function formatQuantity(value: number): string {
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
  return `${formatted} d`;
}
